<template>
  <div
    v-if="isModifierSet(ModifierType.lisaControl)"
    style="position: absolute; inset: 0; height: 100%; width: 100%; z-index: 2000"
    :style="{
      'pointer-events': active ? 'auto' : 'none',
    }"
  >
    <div
      v-if="active"
      class="w-100 h-100 flex justify-content-center align-items-center"
      style="background-color: rgba(255, 255, 255, 0.7); backdrop-filter: blur(2px)"
    >
      <div>
        <div ref="ring" class="eye-ring" :style="`--segments=${segments.length}`">
          <div class="eye-center">
            <section class="stage">
              <figure ref="ball" class="ball">
                <span class="shadow"></span>
                <span ref="iris" class="iris"></span>
              </figure>
            </section>
          </div>
          <!-- interactive icons -->
          <button
            v-for="(segment, index) in segments"
            :key="`segment${index}`"
            ref="sectorButtons"
            class="sector"
            :class="{ active: slectedSlice === index }"
            :style="{
              transform: `translate(-50%, -50%) translate(
                ${Math.sin(((index * segmentLength + segmentLength / 2) * Math.PI) / 180) * radius}px,
                ${-Math.cos(((index * segmentLength + segmentLength / 2) * Math.PI) / 180) * radius}px
              )`,
            }"
            @click="shootBeamToIcon(sectorButtons?.[index])"
          >
            <i ref="sectorIcons" :class="`fa ${segment.icon}`"></i>
          </button>
          <div ref="beam" class="beam"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount } from "vue";
import { onMounted } from "vue";
import { ref } from "vue";
import PewPew from "@/assets/soundEffects/pew-pew.mp3";
import useSound from "@/functions/useSound";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";
import { useRouter, type RouteLocationRaw } from "vue-router";

const segments: { icon: string; color: string; destination: RouteLocationRaw }[] = [
  {
    icon: "fa-dumpster-fire",
    color: "--p-blue-200",
    destination: "/sqdcBoard/kpi",
  },
  {
    icon: "fa-jedi",
    color: "--p-blue-300",
    destination: {
      name: "loggedInStamp",
    },
  },
  {
    icon: "fa-gamepad",
    color: "--p-blue-400",
    destination: {
      name: "boardManagement",
    },
  },
  {
    icon: "fa-puzzle-piece",
    color: "--p-blue-500",
    destination: "/sqdcBoard/eab",
  },
  {
    icon: "fa-child-combatant",
    color: "--p-blue-600",
    destination: {
      name: "dashboard",
    },
  },
];

const active = ref(false);

const segmentLength = 360 / segments.length;

const { play } = useSound(PewPew);

const ball = ref<HTMLDivElement>();
const iris = ref<HTMLSpanElement>();
const ring = ref<HTMLSpanElement>();

const sectorIcons = ref<HTMLElement[]>();
const sectorButtons = ref<HTMLButtonElement[]>();

const slectedSlice = ref(-1);

const radius = ref(185);

const observer = ref<ResizeObserver>();

function calculateEyeOrientation(e: MouseEvent) {
  {
    if (ball.value == null || iris.value == null) {
      return;
    }

    const rect = ball.value.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Vector from center of the eye to mouse
    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;

    // Clamp distance so iris doesn’t go outside the eyeball
    const maxRadius = rect.width * 0.25; // adjust sensitivity
    const angle = Math.atan2(dy, dx);
    const distance = Math.min(Math.hypot(dx, dy), maxRadius);

    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance;

    const squish = 0.18 * (distance / maxRadius); // 0 → no deformation, up to 18%
    iris.value.style.transform = `
    translate(${x}px, ${y}px)
    rotate(${angle + Math.PI / 2}rad)
    scale(${1 + squish}, ${1 - squish})
    rotate(${-angle - Math.PI / 2}rad)
  `;

    const light = { x: -1, y: -1 };

    const len = Math.hypot(dx, dy) || 1;
    const gx = dx / len;
    const gy = dy / len;
    const lightLen = Math.hypot(light.x, light.y);
    const lx = light.x / lightLen;
    const ly = light.y / lightLen;

    const dot = gx * lx + gy * ly; // -1..1
    const opacity = Math.max(0, dot) * 0.2;
    iris.value.style.setProperty("--highlight-opacity", opacity.toFixed(2));

    // Parallax effect: small opposite move of highlight
    const highlightX = -x * 0.2 + "px";
    const highlightY = -y * 0.2 + "px";

    iris.value.style.setProperty("--highlight-x", highlightX);
    iris.value.style.setProperty("--highlight-y", highlightY);

    // Convert radians (-PI..PI) to 0..360°
    let deg = angle * (180 / Math.PI) + 270 / Math.PI;
    if (deg < 0) deg += 360;

    slectedSlice.value = Math.floor(deg / (360 / segments.length));

    if (ring.value == null) return;

    const segmentGradient = segments
      .map(
        (s, index) =>
          `var(${slectedSlice.value !== index ? s.color : "--p-green-500"}) ${index * segmentLength}deg ${(index + 1) * segmentLength}deg`,
      )
      .join(", ");
    ring.value.style.background = `
    conic-gradient(${segmentGradient}),
    radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.4), transparent 70%),
    radial-gradient(circle at 70% 70%, rgba(0, 0, 0, 0.3), transparent 80%)
  `;

    sectorIcons.value?.forEach((iconRef, index) => {
      if (iconRef == null) return;
      iconRef.classList.remove("fa-bounce");
      if (index === slectedSlice.value) {
        iconRef.classList.add("fa-bounce");
      }
    });
  }
}

async function switchActiveState() {
  if (pressedKeys.value == null || Date.now() - pressedKeys.value > 5000) {
    pressedKeys.value = undefined;
    return;
  }

  active.value = !active.value;

  if (active.value) {
    await nextTick();
    observer.value = new ResizeObserver(() => {
      const ringSize = ring.value?.offsetWidth || 500;
      radius.value = ringSize / 2 - ringSize * 0.125; // 12.5% padding for center eye
    });
    if (ring.value) {
      observer.value.observe(ring.value);
    }
    calculateEyeOrientation(
      new MouseEvent("mousemove", {
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2,
      }),
    );
  }
}
const pressedKeys = ref<number>();

function resetActiveState(e: KeyboardEvent) {
  pressedKeys.value = e.metaKey || e.ctrlKey ? Date.now() : undefined;
  if (e.key === "Escape") {
    active.value = false;
  }
}

const router = useRouter();

function cmdReleased() {
  pressedKeys.value = undefined;
}

onMounted(() => {
  if (isModifierSet(ModifierType.lisaControl)) {
    window.addEventListener("dblclick", switchActiveState);
    window.addEventListener("keyup", cmdReleased, true);
    window.addEventListener("keydown", resetActiveState, true);
  }
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
  if (isModifierSet(ModifierType.lisaControl)) {
    window.removeEventListener("dblclick", switchActiveState);
    window.removeEventListener("keyup", cmdReleased, true);
    window.removeEventListener("keydown", resetActiveState, true);
  }
});

document.addEventListener("mousemove", (e) => calculateEyeOrientation(e));

const beam = ref<HTMLElement>();

function shootBeamToIcon(iconEl?: HTMLElement) {
  if (!iris.value || !ring.value || !iconEl || !beam.value) return;

  const ringRect = ring.value.getBoundingClientRect();

  // center of iris
  const irisRect = iris.value.getBoundingClientRect();
  const cx = irisRect.left + irisRect.width / 2 - ringRect.left;
  const cy = irisRect.top + irisRect.height / 2 - ringRect.top;

  // center of icon
  const iconRect = iconEl.getBoundingClientRect();
  const ix = iconRect.left + iconRect.width / 2;
  const iy = iconRect.top + iconRect.height / 2;

  const dx = ix - cx - ringRect.left;
  const dy = iy - cy - ringRect.top;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  const distance = Math.hypot(dx, dy);

  beam.value.style.setProperty("--beam-length", `${distance}px`);
  beam.value.style.left = `${cx}px`;
  beam.value.style.top = `${cy}px`;

  beam.value.style.transform = `rotate(${angle}deg)`;

  // retrigger animation
  beam.value.classList.remove("active");
  void beam.value.offsetWidth; // force reflow
  beam.value.classList.add("active");

  if (isModifierSet(ModifierType.enableSFX)) {
    play();
  }

  // Add color change when beam "arrives"
  setTimeout(() => {
    iconEl.classList.add("hit");
  }, 250); // beam travel duration (match CSS animation)

  router.push(segments[slectedSlice.value].destination);

  setTimeout(() => {
    active.value = false;
  }, 500);
}
</script>

<style lang="scss" scoped>
.ball {
  display: inline-block;
  width: 100%;
  height: 100%;
  margin: 0;
  border-radius: 50%;
  position: relative;
  background: radial-gradient(circle at 50% 40%, #fcfcfc, #efeff1 66%, #9b5050 100%);
  /* the socket shadow, perfectly parallel */
  box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.35);
}
.ball:after {
  content: "";
  position: absolute;
  top: 15%;
  left: 20%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(255, 255, 255, 0.8),
    rgba(255, 255, 255, 0.8) 14%,
    rgba(255, 255, 255, 0) 24%
  );
  transform: translateX(-80px) translateY(-90px) skewX(-20deg);
}

.iris {
  width: 40%;
  height: 40%;
  margin: 30%;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 50%, #208ab4 0%, #6fbfff 30%, #4381b2 100%);
  transform: translateX(68px) translateY(-60px) skewX(15deg) skewY(2deg);
  position: absolute;
  animation: move-eye-skew 5s ease-out infinite;
}
.iris:before {
  content: "";
  display: block;
  position: absolute;
  width: 37.5%;
  height: 37.5%;
  border-radius: 50%;
  top: 31.25%;
  left: 31.25%;
  background: black;
}
.iris:after {
  content: "";
  display: block;
  position: absolute;
  width: 31.25%;
  height: 31.25%;
  border-radius: 50%;
  top: 18.75%;
  left: 18.75%;
  background: rgba(255, 255, 255, var(--highlight-opacity, 0.2));
  transform: translate(var(--highlight-x, 0), var(--highlight-y, 0));
  transition: transform 0.05s linear; /* smooth movement */
}

.ball .shadow {
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(0, 0, 0, 0.4),
    rgba(0, 0, 0, 0.1) 40%,
    rgba(0, 0, 0, 0) 50%
  );
  transform: rotateX(90deg) translateZ(-150px);
  z-index: -1;
}
.stage {
  width: 80%;
  aspect-ratio: 1/1;
  display: inline-block;
  margin: 25px;
  perspective: 1200px;
  perspective-origin: 50% 50%;
  transform-style: preserve-3d;
  transform: rotateY(0.01deg);
}

.eye-ring {
  border-radius: 50%;
  position: relative;
  width: min(90vw, 500px);
  aspect-ratio: 1 / 1;

  /* subtle 3D/glossy */
  box-shadow:
    inset -20px -20px 40px rgba(0, 0, 0, 0.1),
    inset 20px 20px 40px rgba(255, 255, 255, 0.2),
    0 10px 25px rgba(0, 0, 0, 0.2);

  display: flex;
  justify-content: center;
  cursor: pointer;
  align-items: center;
}
/* eye stays in the middle */
.eye-center {
  position: relative;
  width: 50%;
  aspect-ratio: 1/1;
  border-radius: 50%;
  overflow: hidden;
  // box-shadow: 0 8px 25px rgba(0, 0, 0, 0.35) inset;
  box-shadow:
    inset 6px 6px 15px rgba(0, 0, 0, 0.35),
    inset -6px -6px 15px rgba(255, 255, 255, 0.4);
}

/* sectors = clickable buttons */
.sector {
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: center center;
  width: 15%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;

  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.1)
  );
  box-shadow:
    0 4px 8px rgba(0, 0, 0, 0.25),
    inset 0 2px 4px rgba(255, 255, 255, 0.6);

  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  border: none;
  display: flex;
  justify-content: center;
  cursor: pointer;
  font-size: clamp(1rem, max(2vw, 2vh), 2rem);

  align-items: center;
}

.sector.active {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow:
    0 6px 12px rgba(0, 0, 0, 0.35),
    inset 0 2px 6px rgba(255, 255, 255, 0.7);
}

.beam {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 8px;
  background: linear-gradient(to right, rgba(255, 0, 0, 0.8), transparent);
  transform-origin: left center;
  pointer-events: none;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.beam.active {
  opacity: 1;
  animation: beam-shoot 0.5s forwards;
}

.sector.hit {
  background: radial-gradient(circle at 30% 30%, var(--p-red-400), var(--p-red-700));
  box-shadow:
    0 6px 12px rgba(0, 0, 0, 0.35),
    inset 0 2px 6px rgba(255, 255, 255, 0.6);
  transition: background 0.3s ease;
}

@keyframes beam-shoot {
  0% {
    width: 0;
    opacity: 0.2;
  }
  50% {
    width: var(--beam-length);
    opacity: 1;
  }
  100% {
    width: 0;
    opacity: 0;
  }
}
</style>
